import React, { useState, useEffect } from "react";

import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  useCreateTemplate,
  useSaveAsTemplate,
  useUpdateTemplate,
} from "../../hooks";
import type { Template } from "../../types";
import { toast } from "@shared/ui/use-toast";
import { logEvent } from "@shared/analytics/analytics";
import { PlaceholderSelector } from "../placeholder/PlaceholderSelector";

interface TcreateProps {
  onClose: () => void;
  editTemplate?: Template;
  mode?: "create" | "edit";
}

export const Create: React.FC<TcreateProps> = ({
  onClose,
  editTemplate,
  mode = "create",
}) => {
  const [showCategoryInput, setShowCategoryInput] = useState(false);
  const [categories, setCategories] = useState([
    "Payment",
    "Notification",
    "Marketing",
    "Support",
    "Other",
  ]);
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");

  // Add formState to track current form values
  const [formState, setFormState] = useState({
    channelType: editTemplate?.channelType || "EMAIL",
    format: editTemplate?.format,
    templateName: editTemplate?.templateName || "",
    description: editTemplate?.description || "",
    category: editTemplate?.templateCategory || "",
    tags: editTemplate?.tags || [],
    subject: editTemplate?.content?.subject || "",
    contentPlain: editTemplate?.content?.body || "",
    contentRich: editTemplate?.content?.body || "",
    contentHtml: editTemplate?.content?.body || "",
  });

  const createTemplateMutation = useCreateTemplate();
  const saveAsDraftMutation = useSaveAsTemplate();
  const updateTemplateMutation = useUpdateTemplate();

  const isSubmitting =
    createTemplateMutation.isPending || updateTemplateMutation.isPending;
  const isDraftSaving = saveAsDraftMutation.isPending;

  const formFields: FormField[] = [
    {
      name: "channelType",
      type: "radio",
      label: "Communication Channel",
      required: true,
      options: [
        {
          value: "EMAIL",
          label: "Email",
        },
        {
          value: "TEXT",
          label: "SMS",
        },
        {
          value: "WHATSAPP",
          label: "WhatsApp",
        },
      ],
      defaultValue: formState.channelType,
    },
    {
      name: "format",
      type: "radio",
      label: "Template Format",
      required: true,
      options: [
        {
          value: "plainText",
          label: "PlainText",
        },
        {
          value: "richText",
          label: "RichText",
        },
        {
          value: "html",
          label: "Html",
        },
      ],
      defaultValue: formState.format,
      showWhen: () => {
        const channel = formState.channelType;
        return channel === "EMAIL";
      },
    },
    {
      name: "templateName",
      type: "text",
      label: "Template Name",
      required: true,
      placeholder: "e.g., Payment Confirmation",
      maxLength: 100,
      defaultValue: formState.templateName,
    },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      placeholder: "Brief description of this template's purpose",
      rows: 4,
      maxLength: 500,
      defaultValue: formState.description,
    },
    {
      name: "category",
      type: "text",
      label: "",
      required: true,
      defaultValue: formState.category,
      customRender: (field, form) => (
        <div className="space-y-2">
          <label className="form-label">
            Category <span className="text-red-500">*</span>
          </label>

          {showCategoryInput ? (
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="New category name"
                className="form-input"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const value = e.currentTarget.value.trim();
                    if (value) {
                      setCategories((prev) => [...prev, value]);
                      form.setValue("category", value);
                      handleFieldChange("category", value);
                      // Trigger validation
                      form.trigger("category");
                      setShowCategoryInput(false);
                      e.currentTarget.value = "";
                    }
                  }
                }}
              />
              <button
                type="button"
                onClick={(e) => {
                  const input =
                    e.currentTarget.parentElement?.querySelector("input");
                  const value = input?.value.trim();
                  if (value) {
                    setCategories((prev) => [...prev, value]);
                    form.setValue("category", value);
                    handleFieldChange("category", value);
                    // Trigger validation
                    form.trigger("category");
                    setShowCategoryInput(false);
                    if (input) input.value = "";
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => setShowCategoryInput(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <select
                value={form.watch("category") || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  form.setValue("category", value);
                  handleFieldChange("category", value);
                  // Trigger validation
                  form.trigger("category");
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-- Select Category --</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowCategoryInput(true)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Add
              </button>
            </div>
          )}
        </div>
      ),
    },
    {
      name: "tags",
      type: "multi-select",
      label: "",
      defaultValue: formState.tags,
      customRender: (field, form) => (
        <div className="space-y-3">
          <label className="form-label">Tags</label>

          <div className="flex gap-2">
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (newTag.trim() && !tags.includes(newTag.trim())) {
                    const updatedTags = [...tags, newTag.trim()];
                    setTags(updatedTags);
                    form.setValue("tags", updatedTags);
                    handleFieldChange("tags", updatedTags);
                    setNewTag("");
                  }
                }
              }}
              placeholder="Add tag"
              className="form-input"
            />
            <button
              type="button"
              onClick={() => {
                if (newTag.trim() && !tags.includes(newTag.trim())) {
                  const updatedTags = [...tags, newTag.trim()];
                  setTags(updatedTags);
                  form.setValue("tags", updatedTags);
                  handleFieldChange("tags", updatedTags);
                  setNewTag("");
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Add
            </button>
          </div>

          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => {
                      const updatedTags = tags.filter((_, i) => i !== index);
                      setTags(updatedTags);
                      form.setValue("tags", updatedTags);
                      handleFieldChange("tags", updatedTags);
                    }}
                    className="ml-1 hover:text-blue-900"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      name: "subject",
      type: "text",
      label: "Subject",
      placeholder: "Enter template subject",
      defaultValue: formState.subject,
      required: formState.channelType === "EMAIL",
      showWhen: () => formState.channelType === "EMAIL",
    },
    {
      name: "placeholderSelector",
      type: "select",
      label: "",
      customRender: (field, form) => (
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            Insert Placeholder
          </label>

          <PlaceholderSelector
            onSelectPlaceholder={(placeholder) => {
              let targetField = "contentPlain"; // default
              if (formState.format === "richText") {
                targetField = "contentRich";
              } else if (formState.format === "html") {
                targetField = "contentHtml";
              }

              const currentContent = form.getValues(targetField) || "";
              const newContent = currentContent + placeholder;
              form.setValue(targetField, newContent);
              handleFieldChange(targetField, newContent);
            }}
          />
        </div>
      ),
      showWhen: () => formState.contentRich !== undefined,
    },
    {
      name: "contentPlain",
      type: "textarea",
      label: "Content (Plain Text)",
      required: formState.format === "plainText",
      placeholder: "Enter your plain text content here...",
      rows: 10,
      defaultValue: formState.contentPlain,
      showWhen: () => formState.format === "plainText",
      helperText: "Plain text format - no formatting options available.",
    },
    {
      name: "contentRich",
      type: "rich-text",
      label: "Content (Rich Text)",
      required: formState.format === "richText",
      placeholder: "Enter your rich text content here...",
      defaultValue: formState.contentRich,
      showWhen: () => formState.format === "richText",
      helperText: "Rich text with formatting toolbar available.",
    },
    {
      name: "contentHtml",
      type: "html-text",
      label: "Content (HTML)",
      required: formState.format === "html",
      placeholder: "Enter your HTML content here...",
      defaultValue: formState.contentHtml,
      showWhen: () => formState.format === "html",
      helperText: "Raw HTML editing mode for advanced users.",
    },
  ];

  // Handle field changes to update formState
  const handleFieldChange = (fieldName: string, value: any) => {
    setFormState((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    // Reset format to richText when switching from TEXT to EMAIL/WHATSAPP
    if (fieldName === "channelType" && value === "TEXT") {
      setFormState((prev) => ({
        ...prev,
        format: "plainText", // SMS only supports plain text
      }));
      form.setValue("format", "plainText");
    }
  };

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
  });

  useEffect(() => {
    if (editTemplate && mode === "edit") {
      setTags(editTemplate.tags || []);

      if (
        editTemplate.templateCategory &&
        !categories.includes(editTemplate.templateCategory)
      ) {
        setCategories((prev) => [...prev, editTemplate.templateCategory!]);
      }

      // Update formState with template data
      setFormState({
        channelType: editTemplate.channelType || "EMAIL",
        format: editTemplate.format || "richText",
        templateName: editTemplate.templateName || "",
        description: editTemplate.description || "",
        category: editTemplate.templateCategory || "",
        tags: editTemplate.tags || [],
        subject: editTemplate.content?.subject || "",
        contentPlain: editTemplate.content?.body || "",
        contentRich: editTemplate.content?.body || "",
        contentHtml: editTemplate.content?.body || "",
      });
    }
  }, [editTemplate, mode]);

  const transformFormDataToTemplate = (data: any): Template => {
    const getContentByFormat = () => {
      switch (formState.format) {
        case "plainText":
          return data.contentPlain || "";
        case "richText":
          return data.contentRich || "";
        case "html":
          return data.contentHtml || "";
        default:
          return (
            data.contentPlain || data.contentRich || data.contentHtml || ""
          );
      }
    };

    return {
      id: editTemplate?.id,
      templateName: data.templateName,
      channelType: data.channelType,
      templateCategory: data.category,
      content: {
        body: getContentByFormat(),
        subject: data.subject || "",
      },
      tags: tags,
      description: data.description,
      isDraft: editTemplate?.isDraft || false,
      remoteUtilityId: 0,
      templateAlias: "",
      version: 0,
      isActive: false,
      templateType: 0,
      templateTypeDisplay: "",
      createdUserRemoteName: "",
      lastModifiedUserRemoteName: "",
      createdDate: "",
      lastModifiedDate: "",
      format: formState.format,
      type: "default",
      status: "draft",
    };
  };

  const handleSaveDraft = async (data: any) => {
    try {
      const template = transformFormDataToTemplate(data);

      if (mode === "edit" && editTemplate?.id) {
        await updateTemplateMutation.mutateAsync({
          ...template,
          isDraft: true,
        });
        toast({
          title: "Success",
          description: "Template updated and saved as draft!",
          variant: "default",
        });
      } else {
        await saveAsDraftMutation.mutateAsync(template, {
          onSuccess: () => {
            logEvent("Templated Saved as Draft", {
              ...template,
            });
          },
        });
      }

      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to save as draft",
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (data: any) => {
    try {
      const template = transformFormDataToTemplate(data);

      if (mode === "edit" && editTemplate?.id) {
        await updateTemplateMutation.mutateAsync({
          ...template,
          isDraft: false,
        });
        toast({
          title: "Success",
          description: "Template updated successfully!",
          variant: "default",
        });

        logEvent("Template Edited", {
          ...template,
        });
      } else {
        await createTemplateMutation.mutateAsync({
          template: {
            ...template,
            isDraft: false,
          },
          isDraft: false,
        });
        toast({
          title: "Success",
          description: "Template created successfully!",
          variant: "default",
        });
        logEvent("Template Created", {
          ...template,
        });
      }

      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || `Failed to ${mode} template`,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 p-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="page-title">
                {mode === "edit" ? "Edit Template" : "Create New Template"}
              </h1>
              <p className="page-subtitle">
                {mode === "edit"
                  ? "Update your message template for consistent communication."
                  : "Create a new message template for consistent communication."}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-2">
        <DynamicForm
          fields={formFields}
          form={form}
          onSubmit={handleSubmit}
          onFieldChange={handleFieldChange}
          config={{
            layout: "vertical",
            spacing: "comfortable",
            showSubmitButton: false,
            showResetButton: false,
            classes: {
              form: "space-y-6",
              button: "px-4 py-2 rounded-md font-medium transition-colors",
              buttonContainer:
                "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
              input:
                "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
              textarea:
                "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
              select:
                "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
              label: "block text-sm font-medium text-gray-700 mb-1",
              error: "text-sm text-red-600 mt-1",
              container: "space-y-1",
            },
          }}
        />

        <div className="flex items-center justify-between border-gray-200">
          <button
            type="button"
            onClick={() => handleSaveDraft(form.getValues())}
            disabled={isDraftSaving || isSubmitting}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {isDraftSaving ? (
              <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
            ) : null}
            {isDraftSaving ? "Saving..." : "Save as Draft"}
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting || isDraftSaving}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => form.handleSubmit(handleSubmit)()}
              disabled={isSubmitting || isDraftSaving}
              className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <span className="text-lg">{mode === "edit" ? "✓" : "+"}</span>
              )}
              {isSubmitting
                ? `${mode === "edit" ? "Updating" : "Creating"}...`
                : `${mode === "edit" ? "Update" : "Create"} Template`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
